import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { FaPlus, FaEdit, FaTrash, FaCheck, FaTimes } from 'react-icons/fa';

export default function AdminAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', is_active: true });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  async function fetchAnnouncements() {
    setLoading(true);
    const { data } = await supabase
      .from('announcements')
      .select('*')
      .order('created_at', { ascending: false });
    setAnnouncements(data || []);
    setLoading(false);
  }

  function openForm(announcement = null) {
    if (announcement) {
      setForm({
        title: announcement.title,
        content: announcement.content,
        is_active: announcement.is_active,
      });
      setEditingId(announcement.id);
    } else {
      setForm({ title: '', content: '', is_active: true });
      setEditingId(null);
    }
    setShowForm(true);
    setError('');
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    if (!form.title.trim() || !form.content.trim()) {
      setError('Title and content are required.');
      setSaving(false);
      return;
    }
    if (editingId) {
      await supabase
        .from('announcements')
        .update({ ...form, updated_at: new Date() })
        .eq('id', editingId);
    } else {
      await supabase
        .from('announcements')
        .insert([{ ...form }]);
    }
    setShowForm(false);
    setSaving(false);
    setForm({ title: '', content: '', is_active: true });
    setEditingId(null);
    fetchAnnouncements();
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this announcement?')) return;
    await supabase.from('announcements').delete().eq('id', id);
    fetchAnnouncements();
  }

  return (
    <div className="container py-5" style={{maxWidth: 800}}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold">Manage Announcements</h2>
        <button className="btn btn-success rounded-pill px-4" onClick={() => openForm()}><FaPlus className="me-2" />Add Announcement</button>
      </div>
      {showForm && (
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-body">
            <form onSubmit={handleSave}>
              <div className="mb-3">
                <label className="form-label">Title</label>
                <input type="text" className="form-control" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required />
              </div>
              <div className="mb-3">
                <label className="form-label">Content</label>
                <textarea className="form-control" rows={4} value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} required />
              </div>
              <div className="form-check mb-3">
                <input className="form-check-input" type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} id="activeCheck" />
                <label className="form-check-label" htmlFor="activeCheck">Active</label>
              </div>
              {error && <div className="alert alert-danger">{error}</div>}
              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary rounded-pill px-4" disabled={saving}>{saving ? 'Saving...' : (editingId ? 'Update' : 'Add')}</button>
                <button type="button" className="btn btn-secondary rounded-pill px-4" onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {loading ? (
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : announcements.length === 0 ? (
        <div className="alert alert-info">No announcements found.</div>
      ) : (
        <div className="list-group">
          {announcements.map(a => (
            <div key={a.id} className="list-group-item list-group-item-action mb-3 rounded-4 shadow-sm border-0 p-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="fw-bold mb-0">{a.title}</h5>
                <div className="d-flex gap-2">
                  <button className="btn btn-sm btn-outline-primary" onClick={() => openForm(a)} title="Edit"><FaEdit /></button>
                  <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(a.id)} title="Delete"><FaTrash /></button>
                </div>
              </div>
              <div className="mb-2 text-secondary">{a.content}</div>
              <div className="d-flex gap-3 align-items-center small">
                <span className={a.is_active ? 'text-success' : 'text-danger'}>{a.is_active ? <FaCheck /> : <FaTimes />} {a.is_active ? 'Active' : 'Inactive'}</span>
                <span className="text-muted">Created: {new Date(a.created_at).toLocaleString()}</span>
                {a.updated_at && <span className="text-muted">Updated: {new Date(a.updated_at).toLocaleString()}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
} 